/**
 * Phase 1.7 — confirm-then-delete dialog.
 *
 * Wraps shadcn's AlertDialog so v2 cards can offer a one-click archive
 * with a clear confirmation step. Soft-delete on the server side, so the
 * label is "Archive" (matches the v1 vocabulary the existing pages and
 * docs use) — but functionally it's a DELETE.
 */
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { authFetch } from '@/lib/auth';
import { toast } from 'sonner';

export interface DeleteEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** DELETE target prefix. The dialog appends `/${entityId}` automatically. */
  endpoint: string;
  entityId: string;
  /** Entity name shown in the confirmation copy. */
  entityName: string;
  /** Override the action button text. Defaults to "Archive". */
  actionLabel?: string;
  /** Override the description copy. */
  description?: string;
  onDeleted?: () => void;
}

export function DeleteEntityDialog({
  open,
  onOpenChange,
  endpoint,
  entityId,
  entityName,
  actionLabel,
  description,
  onDeleted,
}: DeleteEntityDialogProps): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await authFetch(`${endpoint}/${entityId}`, { method: 'DELETE' });
      toast.success(`${entityName} archived`);
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to archive');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive {entityName}?</AlertDialogTitle>
          <AlertDialogDescription>
            {description ?? 'This will hide it from active lists. The record stays in the database and can be restored.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-destructive text-destructive-foreground"
          >
            {submitting ? 'Archiving…' : (actionLabel ?? 'Archive')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
